export const isBatchSafeRowAction = (action, options = {}) => {
    const code = action?.code || action?.actionName || (typeof action?.name === 'string' ? action.name : undefined);
    if (!code || action?.batchable === false || ['actionCreate', 'actionEdit', 'actionClone'].includes(code)) return false;
    // A row-level delete belongs in the contextual menu when the grid has no
    // separate Delete button. Keep it out only when that CRUD control already exists.
    if (code === 'actionDelete' && !options.explicitlyConfigured && !options.allowDelete) return false;
    if (options.explicitlyConfigured || action.batchable === true) return true;
    if (options.hasCustomFrontendHandler) return false;
    return !Boolean(action.navigateLink || action.openPopup || action.popup || action.onClick);
};

export const canDiscoverBatchActions = (options = {}) => options.autoBatchActions !== false;

const BATCH_ACTION_ICONS = {
    actionDelete: 'trash',
    actionCancel: 'close',
    actionClose: 'check',
    actionPost: 'check',
    actionApprove: 'check',
    actionReject: 'clear',
    actionRelease: 'selectall',
    actionActive: 'check',
    actionDeactive: 'close',
    actionLock: 'lock',
    actionUnlock: 'unlock',
    actionRetry: 'repeat',
    actionForceRetry: 'repeat',
    actionReimportOrder: 'repeat',
    actionDismiss: 'clear',
    actionMarkSkipped: 'clear',
    actionCreateItemFromChannel: 'product',
    actionUnmapMapping: 'unpin',
    actionIgnoreMapping: 'unselectall',
    actionRestoreMapping: 'revert',
    actionDisableSync: 'inactivefolder',
    actionEnableSync: 'activefolder',
    actionSaleActivate: 'check',
    actionSaleDeactivate: 'close',
    actionUpdateActive: 'check',
    actionUpdateInactive: 'inactivefolder',
    actionUpdateSaleActive: 'check',
    actionUpdateSaleInactive: 'close',
    actionUpdateCookingActive: 'bulletlist',
    actionUpdateCookingInactive: 'clear',
    actionUpdateCookingTicketActive: 'print',
    actionUpdateCookingTicketInactive: 'clear',
    actionUpdateServiceTicketActive: 'print',
    actionUpdateServiceTicketInactive: 'clear'
};

export const getBatchActionIcon = (action = {}) => action.icon || BATCH_ACTION_ICONS[action.code] || 'bulletlist';

export const isBatchOnlyRowActions = (display) => display === 'batch';

// The server normally returns { name, visible } for a row action.  Keep this
// tolerant of the other shapes accepted by AntGrid configuration as well, so
// the action set is always derived from the selected row data rather than from
// whichever grid action happened to be inspected first.
const getActionCode = (action) => action?.code || action?.actionName || (typeof action?.name === 'string' ? action.name : undefined);

const getVisibleRowActionCodes = (rows) => {
    const codes = new Set();
    (rows instanceof Array ? rows : []).forEach((row) => {
        if (!(row?.actions instanceof Array)) return;
        row.actions.forEach((action) => {
            const code = getActionCode(action);
            if (code && Boolean(action.visible)) codes.add(code);
        });
    });
    return codes;
};

const BATCH_ACTION_ROW_RULES = {
    actionUpdateActive: { field: 'is_active', value: 0 },
    actionUpdateInactive: { field: 'is_active', value: 1 },
    actionUpdateSaleActive: { field: 'use_AR', value: 0 },
    actionUpdateSaleInactive: { field: 'use_AR', value: 1 },
    actionUpdateCookingActive: { field: 'serve_by_cooking', value: 0 },
    actionUpdateCookingInactive: { field: 'serve_by_cooking', value: 1 },
    actionUpdateCookingTicketActive: { field: 'is_print_cooking_ticket', value: 0 },
    actionUpdateCookingTicketInactive: { field: 'is_print_cooking_ticket', value: 1 },
    actionUpdateServiceTicketActive: { field: 'is_print_service_ticket', value: 0 },
    actionUpdateServiceTicketInactive: { field: 'is_print_service_ticket', value: 1 },
    actionUpdateSalable: { field: 'is_salable', value: 0 },
    actionUpdateUnSalable: { field: 'is_salable', value: 1 }
};

const isRuleEligible = (row, actionCode) => {
    const rule = BATCH_ACTION_ROW_RULES[actionCode];
    if (!rule || row?.[rule.field] === undefined || row?.[rule.field] === null) return true;
    return Number(row[rule.field]) === rule.value;
};

export const resolveGridSelection = (options = {}) => {
    if (options.selectable === false) return false;
    const legacySelectable =
        options.legacySelectable !== undefined
            ? Boolean(options.legacySelectable)
            : options.selectable !== undefined
            ? Boolean(options.selectable)
            : Boolean(options.showEdit || options.showDelete);
    return legacySelectable || options.hasBatchActionCandidates === true;
};

export const getBatchActionStates = (actions, rows) => {
    const selectedRows = rows instanceof Array ? rows : [];
    const hasRowActionMetadata = selectedRows.some((row) => row?.actions instanceof Array);
    return (actions instanceof Array ? actions : []).reduce((states, action) => {
        const minSelection = action.minSelection !== undefined ? Number(action.minSelection) : 1;
        const maxSelection = action.maxSelection !== undefined ? Number(action.maxSelection) : null;
        // Explicitly declared batch actions are validated by batch-execute/prepare.
        // Do not let a stale/incomplete row.actions payload prevent the menu from
        // appearing; auto-discovered actions retain strict per-row eligibility.
        const requiresRowAction = action.requireRowAction &&
            (action.allowMissingRowActions !== true || (action.respectRowActions === true && hasRowActionMetadata));
        const eligibleRows = requiresRowAction
            ? selectedRows.filter(
                  (row) =>
                      (!(row.actions instanceof Array) && action.allowMissingRowActions === true) ||
                      (row.actions instanceof Array &&
                          row.actions.some((rowAction) => getActionCode(rowAction) === action.code && Boolean(rowAction.visible)))
              )
            : action.allowMissingRowActions === true
            ? selectedRows.filter((row) => isRuleEligible(row, action.code))
            : selectedRows;
        const countIsValid = selectedRows.length >= minSelection && (!maxSelection || selectedRows.length <= maxSelection);
        const eligibilityIsValid =
            action.eligibility === 'partial' || action.allowPartial === true
                ? eligibleRows.length > 0
                : selectedRows.length > 0 && eligibleRows.length === selectedRows.length;

        states[action.code] = {
            enabled: countIsValid && eligibilityIsValid,
            selectedCount: selectedRows.length,
            eligibleCount: eligibleRows.length,
            ineligibleCount: selectedRows.length - eligibleRows.length
        };
        return states;
    }, {});
};

export const getContextualBatchActions = (actions, rows) => {
    const selectedRows = rows instanceof Array ? rows : [];
    if (selectedRows.length <= 0) return [];

    const batchActions = actions instanceof Array ? actions : [];
    const states = getBatchActionStates(batchActions, selectedRows);
    const visibleRowActionCodes = getVisibleRowActionCodes(selectedRows);
    const hasRowActionMetadata = selectedRows.some((row) => row?.actions instanceof Array);

    // This is deliberately an outer join.  An action is shown when at least
    // one selected row exposes it; it is disabled (rather than removed) when
    // another selected row does not.  Non-row actions (AntSearchPage) retain
    // their existing eligibility behaviour.
    return batchActions
        .filter((action) => {
            const code = getActionCode(action);
            const state = states[code];
            if (!state) return false;
            return action.requireRowAction
                ? visibleRowActionCodes.has(code) ||
                    (action.allowMissingRowActions === true && (action.respectRowActions !== true || !hasRowActionMetadata))
                : state.eligibleCount > 0;
        })
        .map((action) => ({ ...action, ...states[getActionCode(action)] }));
};

const getRowPermission = (value, field) => {
    if (value === undefined || value === null) return true;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'object' && value[field] !== undefined) return Boolean(value[field]);
    return true;
};

export const getSelectionActionPermissions = (rows, options = {}) => {
    const selectedRows = rows instanceof Array ? rows : [];
    const selectedRow = selectedRows.length === 1 ? selectedRows[0] : null;
    return {
        enableEdit:
            options.canEdit !== false &&
            Boolean(selectedRow) &&
            getRowPermission(selectedRow.edit, 'isEditable'),
        enableDelete:
            options.canDelete !== false &&
            selectedRows.length > 0 &&
            selectedRows.every((row) => getRowPermission(row.delete, 'isDeletable'))
    };
};

export default {
    isBatchSafeRowAction,
    canDiscoverBatchActions,
    getBatchActionIcon,
    isBatchOnlyRowActions,
    resolveGridSelection,
    getBatchActionStates,
    getContextualBatchActions,
    getSelectionActionPermissions
};
